import { useCallback, useSyncExternalStore, type ReactNode } from "react"

const relativeTimeFormatter = new Intl.RelativeTimeFormat("en", {
  numeric: "always",
  style: "long",
})

const subscribers = new Set<() => void>()
let now = Date.now()
let alignmentTimeout: ReturnType<typeof setTimeout> | undefined
let minuteInterval: ReturnType<typeof setInterval> | undefined

function notifySubscribers() {
  for (const subscriber of subscribers) subscriber()
}

function stopClock() {
  if (alignmentTimeout !== undefined) {
    clearTimeout(alignmentTimeout)
    alignmentTimeout = undefined
  }
  if (minuteInterval !== undefined) {
    clearInterval(minuteInterval)
    minuteInterval = undefined
  }
}

function startClock() {
  now = Date.now()
  alignmentTimeout = setTimeout(
    () => {
      alignmentTimeout = undefined
      now = Date.now()
      minuteInterval = setInterval(() => {
        now = Date.now()
        notifySubscribers()
      }, 60_000)
      notifySubscribers()
    },
    60_000 - (now % 60_000),
  )
}

function subscribe(subscriber: () => void) {
  if (subscribers.size === 0) startClock()
  subscribers.add(subscriber)

  return () => {
    subscribers.delete(subscriber)
    if (subscribers.size === 0) stopClock()
  }
}

function getServerSnapshot() {
  return ""
}

export function formatGuildEventRelativeTime(
  startAt: string | Date,
  currentTime: number | Date,
): string {
  const startAtMs =
    startAt instanceof Date ? startAt.getTime() : new Date(startAt).getTime()
  const nowMs =
    currentTime instanceof Date ? currentTime.getTime() : currentTime

  if (!Number.isFinite(startAtMs) || !Number.isFinite(nowMs)) return ""

  const difference = startAtMs - nowMs
  if (difference > 0 && difference < 60_000) return "in less than a minute"
  if (difference <= 0 && difference > -60_000) return "less than a minute ago"

  const absoluteDifference = Math.abs(difference)
  const [unit, unitMs] =
    absoluteDifference >= 86_400_000
      ? (["day", 86_400_000] as const)
      : absoluteDifference >= 3_600_000
        ? (["hour", 3_600_000] as const)
        : (["minute", 60_000] as const)
  const magnitude = Math.floor(absoluteDifference / unitMs)

  return relativeTimeFormatter.format(
    difference > 0 ? magnitude : -magnitude,
    unit,
  )
}

function useGuildEventRelativeTime(startAt: string): string {
  const getSnapshot = useCallback(
    () => formatGuildEventRelativeTime(startAt, now),
    [startAt],
  )
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

function ValidGuildEventRelativeTime({ startAt }: { startAt: string }) {
  const relativeText = useGuildEventRelativeTime(startAt)

  return (
    <span
      className="shrink-0 text-xs text-muted-foreground"
      data-guild-event-relative-time
    >
      {relativeText}
    </span>
  )
}

export function GuildEventRelativeTime({
  startAt,
}: {
  startAt: string
}): ReactNode {
  if (!Number.isFinite(new Date(startAt).getTime())) return null
  return <ValidGuildEventRelativeTime startAt={startAt} />
}
